/**
 * Boundary Adjust Panel Component
 *
 * Visual panel showing boundary adjustment suggestions based on participant locations.
 * Shows current centroid vs center drift and suggested auto-adjustments.
 */

import type { GeoLocation } from '@/lib/types'
import type { AdjustmentResult } from '@/lib/geo'

interface BoundaryAdjustPanelProps {
  sectorName: string
  currentCenter: GeoLocation
  currentRadius: number
  suggestion: AdjustmentResult
  onApply: (newCenter?: GeoLocation, newRadius?: number) => void
  onDismiss: () => void
}

export function BoundaryAdjustPanel({
  sectorName,
  currentCenter,
  currentRadius,
  suggestion,
  onApply,
  onDismiss,
}: BoundaryAdjustPanelProps) {
  if (!suggestion.needsAdjustment) return null

  const hasNewCenter = suggestion.suggestedCenter !== undefined
  const hasNewRadius = suggestion.suggestedRadius !== undefined

  // Calculate the changes
  const centerChange = hasNewCenter
    ? calculateDistance(currentCenter, suggestion.suggestedCenter!)
    : 0
  const radiusChange = hasNewRadius
    ? suggestion.suggestedRadius! - currentRadius
    : 0

  const handleApply = () => {
    onApply(
      hasNewCenter ? suggestion.suggestedCenter : undefined,
      hasNewRadius ? suggestion.suggestedRadius : undefined
    )
  }

  return (
    <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/30 rounded-lg p-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h4 className="text-sm font-medium text-white flex items-center gap-1.5">
            <span>🎯</span> Boundary Adjustment Suggested
          </h4>
          <p className="text-xs text-white/60 mt-0.5">{sectorName}</p>
        </div>
        <button
          onClick={onDismiss}
          className="p-0.5 text-white/40 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Reason */}
      {suggestion.reason && (
        <p className="text-xs text-orange-300/90 mb-3">
          {suggestion.reason}
        </p>
      )}

      {/* Changes preview */}
      <div className="bg-black/20 rounded-lg p-2 mb-3 space-y-1.5">
        {hasNewCenter && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/60">Move center:</span>
            <span className="text-white font-mono">
              {Math.round(centerChange)}m
              <span className="text-white/40 ml-1">
                ({suggestion.suggestedCenter!.latitude.toFixed(5)}, {suggestion.suggestedCenter!.longitude.toFixed(5)})
              </span>
            </span>
          </div>
        )}
        {hasNewRadius && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/60">
              {radiusChange > 0 ? 'Increase' : 'Decrease'} radius:
            </span>
            <span className="text-white font-mono">
              {currentRadius}m <span className="text-white/40">→</span>{' '}
              <span className={radiusChange > 0 ? 'text-green-400' : 'text-orange-400'}>
                {suggestion.suggestedRadius}m
              </span>
              <span className="text-white/40 ml-1">
                ({radiusChange > 0 ? '+' : ''}{Math.round(radiusChange)}m)
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Visual preview */}
      <div className="flex items-center justify-center mb-3">
        <BoundaryPreview
          currentCenter={currentCenter}
          currentRadius={currentRadius}
          suggestedCenter={suggestion.suggestedCenter}
          suggestedRadius={suggestion.suggestedRadius}
        />
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={onDismiss}
          className="flex-1 py-1.5 bg-white/10 text-white/70 rounded text-xs font-medium
            hover:bg-white/20 transition-colors"
        >
          Dismiss
        </button>
        <button
          onClick={handleApply}
          className="flex-1 py-1.5 bg-orange-500 text-white rounded text-xs font-medium
            hover:bg-orange-600 transition-colors"
        >
          Apply Changes
        </button>
      </div>
    </div>
  )
}

// ============= Sub-components =============

function BoundaryPreview({
  currentCenter,
  currentRadius,
  suggestedCenter,
  suggestedRadius,
}: {
  currentCenter: GeoLocation
  currentRadius: number
  suggestedCenter?: GeoLocation
  suggestedRadius?: number
}) {
  // Simple SVG preview showing current vs suggested boundary
  const size = 80
  const padding = 10
  const viewBox = size + padding * 2

  // Normalize to fit in view
  const maxRadius = Math.max(currentRadius, suggestedRadius ?? currentRadius) * 1.2
  const scale = (size / 2) / maxRadius

  const currentR = currentRadius * scale
  const suggestedR = (suggestedRadius ?? currentRadius) * scale

  // Calculate offset if center changes
  let offsetX = 0
  let offsetY = 0
  if (suggestedCenter) {
    const dx = (suggestedCenter.longitude - currentCenter.longitude) * 111000 * Math.cos(currentCenter.latitude * Math.PI / 180)
    const dy = (suggestedCenter.latitude - currentCenter.latitude) * 111000
    offsetX = dx * scale
    offsetY = -dy * scale // Y is inverted in SVG
  }

  const centerX = viewBox / 2
  const centerY = viewBox / 2

  return (
    <svg width={viewBox} height={viewBox} className="rounded-lg bg-black/30">
      {/* Current boundary (dashed) */}
      <circle
        cx={centerX}
        cy={centerY}
        r={currentR}
        fill="none"
        stroke="#f20d80"
        strokeWidth="1.5"
        strokeDasharray="4 2"
        opacity="0.5"
      />

      {/* Suggested boundary */}
      <circle
        cx={centerX + offsetX}
        cy={centerY + offsetY}
        r={suggestedR}
        fill="rgba(249, 115, 22, 0.1)"
        stroke="#f97316"
        strokeWidth="2"
      />

      {/* Current center */}
      <circle
        cx={centerX}
        cy={centerY}
        r="3"
        fill="#f20d80"
      />

      {/* Suggested center (if different) */}
      {suggestedCenter && (
        <>
          <line
            x1={centerX}
            y1={centerY}
            x2={centerX + offsetX}
            y2={centerY + offsetY}
            stroke="#f97316"
            strokeWidth="1"
            strokeDasharray="2 2"
          />
          <circle
            cx={centerX + offsetX}
            cy={centerY + offsetY}
            r="3"
            fill="#f97316"
          />
        </>
      )}

      {/* Labels */}
      <text x={centerX} y={viewBox - 4} textAnchor="middle" fontSize="8" fill="#fff" opacity="0.5">
        Current vs Suggested
      </text>
    </svg>
  )
}

// ============= Utility functions =============

function calculateDistance(point1: GeoLocation, point2: GeoLocation): number {
  const R = 6371000 // Earth radius in meters
  const lat1 = point1.latitude * Math.PI / 180
  const lat2 = point2.latitude * Math.PI / 180
  const deltaLat = (point2.latitude - point1.latitude) * Math.PI / 180
  const deltaLng = (point2.longitude - point1.longitude) * Math.PI / 180

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

export default BoundaryAdjustPanel
